import { ArrowRight, CheckCircle2, Rocket, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

/** Public Cal.com booking page for the 20-min DFY setup call. */
export const SETUP_CALL_URL = 'https://cal.com/magzit/setup-call';

/**
 * Two-front-door section: self-serve vs done-for-you.
 *
 * Surfaces the DFY "Setup Concierge" offer from the GTM plan on the marketing
 * site (previously only in docs). The DFY column is the emphasized path — the
 * non-technical majority converts far better on "we set it up for you" than on
 * "configure your agent yourself". CTA routes to the contact form with a
 * `?topic=dfy` prefill so the intake lands pre-labelled.
 *
 * Pricing here is display-only marketing copy; there is no self-serve checkout
 * for the setup fee yet (intake → we bill manually). Keep the $99 / 72h numbers
 * in sync with docs/GTM-DONE-FOR-YOU-PLAN.md.
 */
const DFY_INCLUDES = [
  'We build your knowledge base from your website, hours, and FAQs',
  'We set up call transfers and your greeting',
  'You keep your number — forward it to us, live the same day',
  'You approve it on a 20-minute call, then it answers 24/7',
];

const SELF_SERVE_INCLUDES = [
  'Guided setup wizard with per-industry templates',
  'Upload your docs, set hours, publish — no code',
  'Voice + website chat on one knowledge base',
  'Go live at your own pace',
];

export function DoneForYouSection(): JSX.Element {
  return (
    <section className="border-y border-border bg-muted/30 py-14 sm:py-20">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Two ways to get started
          </h2>
          <p className="mt-4 text-muted-foreground">
            Do it yourself in minutes, or let us build it for you and hand you
            the keys. Same platform either way.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-4xl items-stretch gap-6 md:grid-cols-2">
          {/* Self-serve */}
          <div className="flex flex-col rounded-2xl border border-border bg-card p-8 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Wrench className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="text-xl font-semibold">Set it up yourself</h3>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              For hands-on owners who want to configure it themselves.
            </p>
            <p className="mt-4 text-3xl font-bold">
              From $39<span className="text-base font-normal text-muted-foreground">/mo</span>
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {SELF_SERVE_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-8">
              <Button asChild variant="outline" className="w-full">
                <Link to="/pricing">See plans</Link>
              </Button>
            </div>
          </div>

          {/* Done-for-you (emphasized) */}
          <div className="relative flex flex-col rounded-2xl border-2 border-primary bg-card p-8 shadow-md">
            <span className="absolute -top-3 left-8 inline-flex items-center rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
              Most popular for busy owners
            </span>
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Rocket className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="text-xl font-semibold">We set it up for you</h3>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Done-for-you setup — it&apos;s answering your phone in 72 hours.
            </p>
            <p className="mt-4 text-3xl font-bold">
              $99<span className="text-base font-normal text-muted-foreground"> one-time</span>
              <span className="ml-2 text-base font-normal text-muted-foreground">
                + your plan
              </span>
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {DFY_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle2
                    className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-8">
              <Button asChild className="w-full">
                <Link to="/contact?topic=dfy">
                  Get it set up for me
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <p className="mt-3 text-center text-sm">
                or{' '}
                <a href={SETUP_CALL_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-[var(--brand-accent)] hover:underline">
                  book a 20-min setup call
                </a>
              </p>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Not answering your calls in 72h? Setup fee refunded.
              </p>
            </div>
          </div>
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground">
          Want us to run it every month — reviewing calls and tuning answers for
          you? Ask about our{' '}
          <Link
            to="/contact?topic=managed"
            className="font-medium text-foreground underline underline-offset-4"
          >
            Managed
          </Link>{' '}
          service.
        </p>
      </div>
    </section>
  );
}
